'use client';
import { SlideFrame } from '../SlideFrame';
import { useResource } from '@/lib/use-resource';
import type { ArchitectureState } from '@/lib/architecture-state';
import { SourcePool } from '@/app/pipeline/_components/architecture/SourcePool';
import { ResolverPinch } from '@/app/pipeline/_components/architecture/ResolverPinch';
import { FactorPillars } from '@/app/pipeline/_components/architecture/FactorPillars';
import { BudgetMeter } from '@/app/pipeline/_components/architecture/BudgetMeter';
import { DailyHealthGrid } from '@/app/pipeline/_components/architecture/DailyHealthGrid';

// Steve directive 2026-05-22: the pipeline slide body should now be the
// LIVE Architecture Flow + Daily Health panels — same components rendered
// at /pipeline?tab=architecture. "Everything in one platform" means the
// deck shows the actual operational dashboard, not stylized stats that
// can drift. Polls /api/architecture-state every 3s during a cron run.
//
// `universe` is accepted for forward-compat (banner/copy) but the live
// numbers in the panels read straight from architecture-state.

export function Slide08Monitoring(_props: { universe: number }) {
  const { data } = useResource<ArchitectureState>('/api/architecture-state', 3_000);

  return (
    <SlideFrame pageNum={11} title="THE VISUALIZER  —  PIPELINE"
      banner="The dashboard the team watches every day. Sources → resolver → factors → budget, with trailing-5-day health alongside.">
      <div className="h-full flex flex-col gap-3 px-6 py-2">
        {!data ? (
          <div className="text-white/40 text-[12px] py-8">Loading architecture state…</div>
        ) : (
          <div className="grid grid-cols-2 gap-4 items-start">

            {/* LEFT — Flow: sources → resolver → factors → budget.
                Container sizes to content (no flex-1 stretch) so the
                black box doesn't extend past its actual rows. */}
            <div className="rounded-md border border-zinc-800 bg-[#0a0e14] p-3">
              <div className="flex items-baseline justify-between">
                <div className="text-[12px] uppercase tracking-wider text-[#c9a84c]">Flow</div>
                <div className="text-[10px] text-white/40">sources → resolver → factors → budget</div>
              </div>
              <div className="mt-2 grid grid-cols-[1fr_72px_1fr] gap-2 items-start">
                <div className="space-y-1">
                  {data.sources.map(s => <SourcePool key={s.id} source={s} />)}
                </div>
                <ResolverPinch resolver={data.resolver} />
                <div className="space-y-1">
                  <FactorPillars
                    pillars={data.pillars}
                    stocksScored={data.scoring.stocksScored}
                    factorCount={data.scoring.factorCount}
                  />
                  <BudgetMeter budget={data.budget} />
                </div>
              </div>
            </div>

            {/* RIGHT — Daily Health 5-day strip */}
            <DailyHealthGrid windowDates={data.windowDates} rows={data.dailyHealth} />

          </div>
        )}
      </div>
    </SlideFrame>
  );
}
